import { useCallback, useEffect, useState } from "react";
import { mcpCall, consumerIdOrThrow } from "../lib/mcp-client";
import { useTranslation } from "../lib/i18n";

// Manage surface (LS-SPEC-03 Manage mode UI). Three sections:
//   1. Saved cards (alexandria.list_saved)
//   2. Waitlist (waitlist.list)
//   3. Pending rating prompts (rating.list_for_consumer)

interface SavedCard {
  save_id: string;
  card_id: string;
  saved_at: number;
}

interface WaitlistEntry {
  entry_id: string;
  vertical: string;
  provider_id: string | null;
  desired_at_unix: number;
  party_size: number;
  status: string;
}

interface RatingPrompt {
  prompt_id: string;
  deal_id: string;
  vertical: string;
  experience_completed_at_unix: number;
}


export function ManageSurface() {
  const [saved, setSaved] = useState<SavedCard[] | null>(null);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[] | null>(null);
  const [prompts, setPrompts] = useState<RatingPrompt[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Saves / waitlist / ratings are legacy secondary panels — the new
  // primary view is Current / Upcoming / Past bookings (rendered from
  // sealed deals). MCP routes for these legacy panels aren't wired
  // through CloudFront yet, so swallow errors quietly and let those
  // panels render empty.
  const load = useCallback(async () => {
    setErr(null);
    const id = (() => { try { return consumerIdOrThrow(); } catch { return null; } })();
    if (!id) {
      setSaved([]); setWaitlist([]); setPrompts([]);
      return;
    }
    const safe = async <T,>(p: Promise<T>, fb: T): Promise<T> => {
      try { return await p; } catch { return fb; }
    };
    const [s, w, r] = await Promise.all([
      safe(mcpCall<{ saves: SavedCard[] }>("alexandria.list_saved",
        { consumer_id: id }, { service: "bff" }), { saves: [] }),
      safe(mcpCall<{ entries: WaitlistEntry[] }>("waitlist.list",
        { consumer_id: id }), { entries: [] }),
      safe(mcpCall<{ prompts: RatingPrompt[] }>("rating.list_for_consumer",
        { consumer_id: id }), { prompts: [] }),
    ]);
    setSaved(s.saves || []);
    setWaitlist(w.entries || []);
    setPrompts(r.prompts || []);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col gap-3 px-4 py-3 overflow-y-auto">
      <header>
        <h1 className="text-xl font-medium text-(--color-ink)">Manage</h1>
        <p className="mt-1 text-xs text-(--color-ink-faint)">
          Your bookings — what's happening now, what's next, and the archive.
          Prompt Triton here to update, cancel, or move a deal.
        </p>
      </header>
      {err && (
        <p className="rounded-md bg-red-900/40 px-2 py-1 text-xs text-red-200">
          {err}
        </p>
      )}

      <BookingSection
        title="Current"
        blurb="Happening now"
        bookings={SAMPLE_BOOKINGS_CURRENT}
        tone="active"
      />
      <BookingSection
        title="Upcoming"
        blurb="Sealed and on the calendar"
        bookings={SAMPLE_BOOKINGS_UPCOMING}
        tone="upcoming"
      />
      <BookingSection
        title="Past trips · archive"
        blurb="Completed — open for receipts, reviews, or to re-book"
        bookings={SAMPLE_BOOKINGS_PAST}
        tone="past"
      />

      <Section title={t("manage.saved_cards")} count={saved?.length ?? 0}>
        {saved?.length === 0 ? (
          <Empty hint="Tap Save on any Alexandria card to find it here." />
        ) : (
          <ul className="space-y-1.5">
            {saved?.map((s) => (
              <SavedRow key={s.save_id} entry={s} onReload={load} />
            ))}
          </ul>
        )}
      </Section>

      <Section title={t("manage.waitlist")} count={waitlist?.length ?? 0}>
        {waitlist?.length === 0 ? (
          <Empty hint="When something's unavailable, Triton can watch a slot for you." />
        ) : (
          <ul className="space-y-1.5">
            {waitlist?.map((e) => (
              <WaitlistRow key={e.entry_id} entry={e} onReload={load} />
            ))}
          </ul>
        )}
      </Section>

      <Section title={t("manage.ratings")} count={prompts?.length ?? 0}>
        {prompts?.length === 0 ? (
          <Empty hint="Rating prompts surface after each booking." />
        ) : (
          <ul className="space-y-1.5">
            {prompts?.map((p) => (
              <RatingRow key={p.prompt_id} prompt={p} onReload={load} />
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}


function Section({
  title, count, children,
}: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
      <h2 className="mb-2 flex items-baseline justify-between text-xs uppercase tracking-wide text-(--color-ink-faint)">
        <span>{title}</span>
        <span className="text-(--color-ink-muted) tabular-nums">{count}</span>
      </h2>
      {children}
    </section>
  );
}


function Empty({ hint }: { hint: string }) {
  return (
    <p className="text-xs text-(--color-ink-faint)">{hint}</p>
  );
}


function SavedRow({
  entry, onReload,
}: { entry: SavedCard; onReload: () => void }) {
  const remove = async () => {
    try {
      await mcpCall("alexandria.unsave_card",
        { consumer_id: consumerIdOrThrow(), card_id: entry.card_id },
        { service: "bff" });
      onReload();
    } catch (e) {
      alert((e as Error).message);
    }
  };
  return (
    <li className="flex items-baseline justify-between gap-2 text-xs">
      <span className="truncate font-mono text-(--color-ink-muted)">
        {entry.card_id}
      </span>
      <button
        onClick={remove}
        className="rounded-(--radius-pill) px-2 py-0.5 text-(--color-ink-faint) hover:text-red-300"
      >
        Remove
      </button>
    </li>
  );
}


function WaitlistRow({
  entry, onReload,
}: { entry: WaitlistEntry; onReload: () => void }) {
  const when = new Date(entry.desired_at_unix * 1000).toLocaleString();
  const cancel = async () => {
    try {
      await mcpCall("waitlist.cancel", { entry_id: entry.entry_id });
      onReload();
    } catch (e) {
      alert((e as Error).message);
    }
  };
  return (
    <li className="flex items-baseline justify-between gap-2 text-xs">
      <span className="flex-1 truncate text-(--color-ink-muted)">
        {entry.vertical} · {entry.party_size}pax · {when}{" "}
        <span className="text-(--color-ink-faint)">[{entry.status}]</span>
      </span>
      {(entry.status === "watching" || entry.status === "slot_opened") && (
        <button
          onClick={cancel}
          className="rounded-(--radius-pill) px-2 py-0.5 text-(--color-ink-faint) hover:text-red-300"
        >
          Cancel
        </button>
      )}
    </li>
  );
}


interface Booking {
  id: string;
  title: string;
  subtitle: string;
  vertical: string;
  when: string;       // human-readable date/time
  city?: string;
  imageURL?: string;
  pricePaid?: number;
  sealedDealId?: string;
}


// Sample data for the Current / Upcoming / Past sections. Real impl
// will query sealed deals from ``midnight.list_for_consumer`` +
// receipt ledger and group by status (active / future / completed).
const SAMPLE_BOOKINGS_CURRENT: Booking[] = [
  {
    id: "bk_kyoto_apr_2026",
    title: "Aoyama Stay Kyoto",
    subtitle: "3-night couples · onsen top floor · check-out tomorrow",
    vertical: "travel.hotel",
    when: "Now → Apr 14",
    city: "Kyoto",
    imageURL: "https://images.unsplash.com/photo-1545569310-a8e2c44ad29c?w=1200",
    pricePaid: 168_000,
    sealedDealId: "deal_b2459e2",
  },
];


const SAMPLE_BOOKINGS_UPCOMING: Booking[] = [
  {
    id: "bk_lambassade_may",
    title: "L'Ambassade · Anniversary",
    subtitle: "Duck confit + Latour by the glass · table for 2 · 19:30",
    vertical: "food.fr",
    when: "Sat May 24 · 19:30",
    city: "Minato",
    imageURL: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200",
    pricePaid: 33_000,
    sealedDealId: "deal_7a90c419",
  },
  {
    id: "bk_yumoto_jun",
    title: "Hakone Yumoto Resort",
    subtitle: "Family · 4-way split · spa + kayak · check-in Saturday",
    vertical: "travel.hotel",
    when: "Jun 7 → Jun 8",
    city: "Hakone",
    imageURL: "https://images.unsplash.com/photo-1545569310-a8e2c44ad29c?w=1200",
    pricePaid: 92_000,
    sealedDealId: "deal_d52b3d7",
  },
  {
    id: "bk_cdg_pickup_may",
    title: "CdG Aoyama · trench + shirt pickup",
    subtitle: "Early-access slot · 11:00 tomorrow",
    vertical: "fashion",
    when: "Tomorrow · 11:00",
    city: "Aoyama",
    imageURL: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1200",
    pricePaid: 119_000,
    sealedDealId: "deal_9f0d678",
  },
];


const SAMPLE_BOOKINGS_PAST: Booking[] = [
  {
    id: "bk_ginza_sushi_sep",
    title: "Sushi Tama (Roppongi)",
    subtitle: "Agent-to-agent bid winner · 7 friends · seared dessert on the house",
    vertical: "food.sushi",
    when: "Sep 12, 2025",
    city: "Tokyo",
    imageURL: "https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=1200",
    pricePaid: 17_500,
    sealedDealId: "deal_3a90fe18",
  },
  {
    id: "bk_okinawa_jul",
    title: "Okinawa Summer · 5 nights",
    subtitle: "Resort + flight bundle · reef snorkeling + family dinners",
    vertical: "travel.package",
    when: "Jul 18–22, 2025",
    city: "Okinawa",
    imageURL: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200",
    pricePaid: 248_000,
    sealedDealId: "deal_0b729e4",
  },
  {
    id: "bk_breville_may",
    title: "Breville Bambino Plus · Rakuten",
    subtitle: "Cross-marketplace authenticity check · ¥8k points applied",
    vertical: "ecommerce",
    when: "May 18, 2025",
    city: "Online",
    imageURL: "https://images.unsplash.com/photo-1517668808822-9ebb02f2a0e6?w=1200",
    pricePaid: 64_000,
    sealedDealId: "deal_8627dea",
  },
];


function BookingSection({
  title, blurb, bookings, tone,
}: { title: string; blurb: string; bookings: Booking[]; tone: "active" | "upcoming" | "past" }) {
  const accent = tone === "active" ? "border-emerald-700/60"
    : tone === "upcoming" ? "border-(--color-accent-gold-dim)/40"
    : "border-white/5";
  return (
    <section className={`rounded-xl border ${accent} bg-(--color-canvas-card) p-3`}>
      <header className="mb-2 flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-medium text-(--color-ink)">{title}</h2>
          <p className="text-[10px] text-(--color-ink-faint)">{blurb}</p>
        </div>
        <span className="text-[10px] text-(--color-ink-muted) tabular-nums">{bookings.length}</span>
      </header>
      {bookings.length === 0 ? (
        <Empty hint="Nothing here yet. Book something on Triton and it'll land here." />
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {bookings.map((bk) => (
            <BookingTile key={bk.id} booking={bk} />
          ))}
        </ul>
      )}
    </section>
  );
}


function BookingTile({ booking }: { booking: Booking }) {
  return (
    <li className="overflow-hidden rounded-(--radius-card) bg-(--color-canvas-elev) ring-1 ring-white/5">
      {booking.imageURL && (
        <div className="aspect-[16/9] w-full bg-stone-900">
          <img
            src={booking.imageURL}
            alt={booking.title}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        </div>
      )}
      <div className="p-2.5 text-xs">
        <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wide text-(--color-ink-faint)">
          <span>{booking.city ?? booking.vertical}</span>
          <span>{booking.when}</span>
        </div>
        <p className="mt-1 line-clamp-2 text-sm font-medium text-(--color-ink)">{booking.title}</p>
        <p className="mt-0.5 line-clamp-2 text-[11px] text-(--color-ink-muted)">{booking.subtitle}</p>
        {booking.pricePaid && (
          <p className="mt-1 text-[11px] text-(--color-ink-faint)">
            ¥{booking.pricePaid.toLocaleString()}
            {booking.sealedDealId && <span className="ml-1 font-mono">· {booking.sealedDealId.slice(0, 12)}…</span>}
          </p>
        )}
      </div>
    </li>
  );
}


function RatingRow({
  prompt, onReload,
}: { prompt: RatingPrompt; onReload: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const submitStar = async (stars: number) => {
    setSubmitting(true);
    try {
      await mcpCall("rating.submit", {
        prompt_id: prompt.prompt_id,
        consumer_id: consumerIdOrThrow(),
        deal_id: prompt.deal_id,
        provider_id: "unknown",     // POC — the prompt should carry this
        vertical: prompt.vertical,
        overall_stars: stars,
      });
      onReload();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <li className="flex items-baseline justify-between gap-2 text-xs">
      <span className="flex-1 truncate font-mono text-(--color-ink-muted)">
        {prompt.deal_id} ({prompt.vertical})
      </span>
      <span className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            disabled={submitting}
            onClick={() => submitStar(s)}
            className="text-(--color-ink-faint) hover:text-(--color-accent-gold) disabled:opacity-40"
            aria-label={`${s} star${s !== 1 ? "s" : ""}`}
          >
            ★
          </button>
        ))}
      </span>
    </li>
  );
}
